import { Check, Loader2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useQualificationRunning } from "@/hooks/useQualificationAnalysis";
import type { QualificationSummary } from "@/types/domain";

/**
 * La calificación: el cruce de la licitación contra los documentos de la empresa,
 * resumido para una fila. Se pinta donde se pintan los matches.
 *
 * El número del anillo son los puntos proyectados, no el score: es lo que el
 * pliego reparte y lo que se puede contrastar contra él.
 */

const TONES = {
  CALIFICA: {
    label: "Calificas",
    box: "bg-emerald-50 dark:bg-emerald-950/30",
    text: "text-emerald-700 dark:text-emerald-300",
    ring: "stroke-emerald-600",
  },
  REVISAR: {
    label: "Por revisar",
    box: "bg-amber-50 dark:bg-amber-950/30",
    text: "text-amber-700 dark:text-amber-300",
    ring: "stroke-amber-500",
  },
  NO_CALIFICA: {
    label: "No calificas",
    box: "bg-rose-50 dark:bg-rose-950/30",
    text: "text-rose-700 dark:text-rose-300",
    ring: "stroke-rose-500",
  },
  ANALYZING: {
    label: "Analizando…",
    box: "bg-sky-50 dark:bg-sky-950/30",
    text: "text-sky-700 dark:text-sky-300",
    ring: "stroke-sky-300 dark:stroke-sky-700",
  },
  PENDING: {
    label: "Sin cruzar",
    box: "bg-slate-100 dark:bg-slate-800/60",
    text: "text-slate-500 dark:text-slate-400",
    ring: "stroke-slate-300 dark:stroke-slate-600",
  },
  WAITING_BASES: {
    label: "Esperando bases",
    box: "bg-slate-100 dark:bg-slate-800/60",
    text: "text-slate-600 dark:text-slate-300",
    ring: "stroke-slate-300 dark:stroke-slate-600",
  },
} as const;

const RING_RADIUS = 13;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** El anillo: se llena según los puntos y lleva el número, o un ícono si no hay. */
function ScoreRing({
  fraction,
  ringClass,
  children,
}: {
  fraction: number;
  ringClass: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative size-8 shrink-0">
      <svg viewBox="0 0 32 32" className="size-8 -rotate-90">
        <circle
          cx="16"
          cy="16"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="3"
          className="stroke-white/80 dark:stroke-slate-900/60"
        />
        <circle
          cx="16"
          cy="16"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${RING_LENGTH * fraction} ${RING_LENGTH}`}
          className={ringClass}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-extrabold tabular-nums text-slate-700 dark:text-slate-200">
        {children}
      </span>
    </div>
  );
}

/** Qué dice la segunda línea del badge. */
function detailLine(qualification: QualificationSummary): string {
  if (qualification.status === "ANALYZING") return "con tus documentos";
  // El correo todavía no existe: es la promesa que decidió Abel (ver la spec del 10-oct).
  if (qualification.status === "WAITING_BASES") return "te enviaremos el informe por correo";
  if (qualification.status === "PENDING") {
    return qualification.hasDocuments ? "ábrela para analizarla" : "sube tus documentos";
  }

  const points =
    qualification.projectedPoints !== null
      ? `${qualification.projectedPoints}/${qualification.availablePoints} pts`
      : null;
  const reason =
    qualification.verdict === "NO_CALIFICA"
      ? (qualification.failedRequirement ?? "falta un requisito obligatorio")
      : qualification.verdict === "REVISAR" && qualification.pendingCount > 0
        ? `falta ${qualification.pendingCount} ${qualification.pendingCount === 1 ? "documento" : "documentos"}`
        : null;
  // Descalificada, los puntos no importan: manda el motivo.
  const parts =
    qualification.verdict === "NO_CALIFICA" ? [reason] : [points, reason];
  if (qualification.isStale) parts.push("desactualizado");

  return parts.filter(Boolean).join(" · ") || "requisitos cubiertos";
}

export function QualificationBadge({
  qualification,
  className,
}: {
  qualification: QualificationSummary;
  className?: string;
}) {
  const running = useQualificationRunning();
  // Mientras corre el análisis de la empresa, lo que no está al día se ve en cola.
  const analyzing =
    qualification.status === "ANALYZING" ||
    (running &&
      (qualification.status === "PENDING" ||
        (qualification.status === "DONE" && qualification.isStale)));
  const tone = analyzing
    ? TONES.ANALYZING
    : qualification.status === "DONE"
      ? TONES[qualification.verdict]
      : TONES[qualification.status];
  const detail = analyzing ? "con tus documentos" : detailLine(qualification);

  let fraction = 0;
  let ringContent: React.ReactNode = "—";
  if (analyzing) {
    ringContent = <Loader2 className="size-3.5 animate-spin text-sky-600" />;
  } else if (qualification.status === "DONE") {
    if (qualification.verdict === "NO_CALIFICA") {
      fraction = 1;
      ringContent = <X className="size-3.5 text-rose-600" />;
    } else if (qualification.projectedPoints !== null && qualification.availablePoints) {
      fraction = Math.min(1, qualification.projectedPoints / qualification.availablePoints);
      ringContent = qualification.projectedPoints;
    } else {
      fraction = 1;
      ringContent = <Check className="size-3.5" />;
    }
  }

  return (
    <div
      title={`${tone.label} · ${detail}`}
      className={cn(
        "flex max-w-full items-center gap-2 rounded-xl py-1.5 pl-1.5 pr-3 text-left",
        tone.box,
        className,
      )}
    >
      <ScoreRing fraction={fraction} ringClass={tone.ring}>
        {ringContent}
      </ScoreRing>
      <div className="min-w-0">
        <div
          className={cn(
            "text-[11px] font-bold uppercase tracking-wider",
            tone.text,
          )}
        >
          {tone.label}
        </div>
        <div className="truncate text-[11px] text-slate-500 dark:text-slate-400">
          {detail}
        </div>
      </div>
    </div>
  );
}
